import {
  Card,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Box,
} from "@mui/material";
import React, { FC, useState } from "react";
import { useParams } from "react-router-dom";
import shiftsService from "services/shiftsService";
import { Shift } from "../../common/models";
import * as Yup from "yup";
import DeleteIcon from "@mui/icons-material/Delete";
import MDButton from "components/MDButton";
import { toast } from "react-toastify";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

export interface ShiftProps {
  crewShifts: Shift[];
  driverId: number;
  reloadEmployee(): any;
}
export interface ShiftErrors {
  dayOfWeek: string;
  timeStart?: string;
  timeEnd?: string;
  driverId: string;
  shiftType: string;
}

const Shifts: FC<ShiftProps> = (props) => {
  const { id } = useParams();
  const [errors, setErrors] = useState<ShiftErrors>();

  const shiftSchema: Yup.ObjectSchema<Shift> = Yup.object().shape({
    dayOfWeek: Yup.string().required(),
    shiftType: Yup.string().required(),
    timeStart: Yup.string().required("Time Start is required."),
    timeEnd: Yup.string().required("Time End is required"),
    driverId: Yup.number().required(),
    crewShiftId: Yup.number(),
  });

  const [shift, setShift] = useState<Shift>({
    dayOfWeek: "Monday",
    shiftType: "1",
    timeStart: new Date(new Date().setHours(8, 0, 0)).toLocaleTimeString("en-US", { hour12: false }),
    timeEnd: new Date(new Date().setHours(17, 0, 0)).toLocaleTimeString("en-US", { hour12: false }),
    driverId: props.driverId,
  });

  function handleOnChange(e: any) {
    setShift({ ...shift, [e.target.name]: e.target.value });
  }
  function addShift() {
    if (!validate()) return;
    shiftsService.addShift({ ...shift, driverId: props.driverId })
    .then((r) => props.reloadEmployee())
    .then(() => toast.success("Shift Added"));
  }

  function validate() {
    try {
      shiftSchema.validateSync(shift, { abortEarly: false });
      setErrors({
        dayOfWeek: "",
        timeStart: "",
        timeEnd: "",
        driverId: "",
        shiftType: "",
      });
      return true;
    } catch (error) {
      let validation = error as Yup.ValidationError;
      const newErrors = validation.inner.reduce((acc: any, cur: any) => {
        acc[cur.path] = cur.message;
        return acc;
      }, {});
      setErrors(newErrors);
      return false;
    }
  }

  function deleteShift(id: number) {
    shiftsService.deleteShift(id)
    .then((r) => props.reloadEmployee())
    .then(() => toast.success("Shift Removed"));
  }
  return (
    <>
      <Grid item xs={12} mt={1}>
        <Card>
        <MDBox p={3}>
        <MDTypography variant="h5">Availability</MDTypography>
      </MDBox>
      <MDBox component="form" pb={3} px={3}>
          <Grid container spacing="2">
            <FormControl sx={{ mt: 2, ml: 1, minWidth: 130 }}>
              <InputLabel id="dayOfWeek">Day of Week</InputLabel>
              <Select
                id="dayOfWeek"
                name="dayOfWeek"
                labelId="dayOfWeek"
                label="Day Of Week"
                required
                value={shift?.dayOfWeek}
                onChange={handleOnChange}
                onBlur={validate}
                error={!!errors?.dayOfWeek}
              >
                <MenuItem value="Monday">Monday</MenuItem>
                <MenuItem value="Tuesday">Tuesday</MenuItem>
                <MenuItem value="Wednesday">Wednesday</MenuItem>
                <MenuItem value="Thursday">Thursday</MenuItem>
                <MenuItem value="Friday">Friday</MenuItem>
                <MenuItem value="Saturday">Saturday</MenuItem>
                <MenuItem value="Sunday">Sunday</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ mt: 2, ml: 1, minWidth: 120 }}>
              <TextField
                name="timeStart"
                label="Start Time"
                type="time"
                InputLabelProps={{ shrink: true }}
                value={shift?.timeStart}
                onChange={handleOnChange}
                onBlur={validate}
                required
                error={!!errors?.timeStart}
                helperText={errors?.timeStart}
              />
            </FormControl>
            <FormControl sx={{ mt: 2, ml: 1, minWidth: 120 }}>
              <TextField
                name="timeEnd"
                label="End Time"
                type="time"
                value={shift?.timeEnd}
                required
                onChange={handleOnChange}
                onBlur={validate}
                InputLabelProps={{ shrink: true }}
                error={!!errors?.timeEnd}
                helperText={errors?.timeEnd}
              />
            </FormControl>
            <FormControl sx={{ mt: 2, ml: 1, minWidth: 120 }}>
              <InputLabel id="statusId">Status</InputLabel>
              <Select
                id="shiftType"
                name="shiftType"
                labelId="Shift Type"
                label="Shift Type"
                required
                value={shift?.shiftType}
                onBlur={validate}
                onChange={handleOnChange}
                error={!!errors?.shiftType}
              >
                <MenuItem value="1">Normal Schedule</MenuItem>
                <MenuItem value="3">Override</MenuItem>
              </Select>
            </FormControl>
            <MDButton sx={{ mt: 2, ml: 1, mb:3, maxHeight: 54 }} variant="contained" color="dark" onClick={addShift}>
              Add
            </MDButton>
          </Grid>
          <Grid>
            <Table className="table table-striped">
              <TableHead>
                <TableRow>
                  <TableCell>WeekDay</TableCell>
                  <TableCell>Start Time</TableCell>
                  <TableCell>End Time</TableCell>
                  <TableCell>Shift Type</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {props.crewShifts &&
                  props.crewShifts.length > 0 &&
                  props.crewShifts.map((s, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell>{s.dayOfWeek}</TableCell>
                        <TableCell>{new Date(s.timeStart!)?.toLocaleTimeString()}</TableCell>
                        <TableCell>{new Date(s.timeEnd!)?.toLocaleTimeString()}</TableCell>
                        <TableCell>{s.shiftType}</TableCell>
                        <TableCell>
                          <IconButton aria-label="delete" onClick={() => deleteShift(s.crewShiftId!)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </Grid>
          </MDBox>
        </Card>
      </Grid>
    </>
  );
};

export default Shifts;
